import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import './ClientIntake.css';

const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;


// File upload constants
const MAX_FILE_SIZE = 225 * 1024 * 1024; // 225MB in bytes
const ALLOWED_FILE_TYPES = [
    'application/pdf',
    'application/msword',
    'application/octet-stream',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'message/rfc822'
];

interface TimelineEvent {
    date: string;
    description: string;
    id: string;
}

interface FormData {
    firstName: string;
    lastName: string;
    email: string;
    wrongdoingParty: string;
    phone: string;
    legalIssue: string;
    description: string;
    timeline: TimelineEvent[];
    files: File[];
}

interface UploadedFile {
    fileName: string;
    downloadUrl: string;
    size: number;
    type: string;
    originalName: string;
}

function ClientIntake() {
    const [formData, setFormData] = useState<FormData>({
        firstName: '',
        lastName: '',
        email: '',
        wrongdoingParty: '',
        phone: '',
        legalIssue: '',
        description: '',
        timeline: [],
        files: []
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [fileError, setFileError] = useState('');

    // File upload helper functions
    const validateFile = (file: File): string | null => {
        if (file.size > MAX_FILE_SIZE) {
            return `File "${file.name}" is too large. Maximum size is 225MB.`;
        }
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
            return `File "${file.name}" is not a supported format. Please upload PDF, Word documents, images, or text files.`;
        }
        return null;
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setFileError('');

        // Validate each file
        for (const file of files) {
            const error = validateFile(file);
            if (error) {
                setFileError(error);
                return;
            }
        }

        // Check total number of files (limit to 10)
        const totalFiles = formData.files.length + files.length;
        if (totalFiles > 10) {
            setFileError('You can upload a maximum of 10 files.');
            return;
        }

        setFormData(prev => ({
            ...prev,
            files: [...prev.files, ...files]
        }));

        // Reset file input
        e.target.value = '';
    };

    const removeFile = (index: number) => {
        setFormData(prev => ({
            ...prev,
            files: prev.files.filter((_, i) => i !== index)
        }));
        setFileError('');
    };



    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        if (!captchaToken) {
            setError('Please complete the CAPTCHA.');
            window.scrollTo({ top: 0, behavior: 'smooth'});
            setIsSubmitting(false)
            return;
        }

        setError('');

        try {
            // Create FormData for file uploads
            const submitData = new FormData();
            
            // Add all form fields
            submitData.append('firstName', formData.firstName);
            submitData.append('lastName', formData.lastName);
            submitData.append('email', formData.email);
            submitData.append('wrongdoingParty', formData.wrongdoingParty);

            // send just the digits
            //const phoneDigitsOnly = formData.phone.replace(/[^\d]/g, '');
            submitData.append('phone', formData.phone);

            submitData.append('legalIssue', formData.legalIssue);
            submitData.append('description', formData.description);
            submitData.append('captchaToken', captchaToken);
            
            // Add timeline as JSON string
            submitData.append('timeline', JSON.stringify(formData.timeline));
            
            // files and file count added below

            // new logic to upload files directly to s3

            let uploadedFiles: UploadedFile[] = [];

            if (formData.files.length > 0)
            {
                const now = new Date();
                const clientFolder = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}_${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}_${String(now.getMinutes()).padStart(2, '0')}_${formData.firstName}_${formData.lastName}`;

                for (const file of formData.files)
                {
                    try
                    {
                        // first, get the upload and download URL
                        const urlResponse = await fetch('/api/upload',{
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                fileName: file.name,
                                fileType: file.type,
                                clientFolder: clientFolder
                            })
                        });

                        if (!urlResponse.ok)
                        {
                            throw new Error(`Failed to get upload URL for ${file.name}`);
                        }

                        const { uploadUrl, downloadUrl, fileName } = await urlResponse.json();

                        // now, upload file directly to s3
                        const uploadResponse = await fetch(uploadUrl, {
                            method: 'PUT', 
                            body: file,
                            headers: {
                                'Content-Type': file.type,
                            },
                        });

                        if (!uploadResponse.ok)
                        {
                            throw new Error(`Failed to upload ${file.name} to S3`);
                        }

                        // finally, store the file info with the download url
                        uploadedFiles.push({
                            fileName,
                            downloadUrl,
                            size: file.size,
                            type: file.type,
                            originalName: file.name
                        });
                    } catch (error) {
                        console.error(`Error uploading ${file.name}:`, error);
                        throw error;
                    }
                }

                submitData.append('clientFolder', clientFolder)
            }
            // Add files
            submitData.append('uploadedFiles', JSON.stringify(uploadedFiles))

            
            
            // Add file count for backend processing
            submitData.append('fileCount', uploadedFiles.length.toString());
            

            //console.log(`Total size: ${totalSize / (1024 * 1024)} MB`);

            // if (totalSize > MAX_TOTAL_SIZE) {
            //     setError(`Total payload size exceeds the maximum limit. Please remove your files, try submitting the form again, and contact our office at (951) 840-1541 in order to upload your files directly.`);
            //     window.scrollTo({ top: 0, behavior: 'smooth'});
            //     setIsSubmitting(false);
            //     return;
            // }

            const response = await fetch('/api/submitForm', {
                method: 'POST',
                body: submitData // Don't set Content-Type header - let browser set it with boundary
            });

            const data = await response.json();

            console.log("Submission received!");

            if (!response.ok) {
                window.scrollTo({ top: 0, behavior: 'smooth'});
                throw new Error(data.message || 'Submission failed');
            }

            setSubmitted(true);
            setFormData({ firstName: '', lastName: '', email: '', wrongdoingParty: '', phone: '', legalIssue: '', description: '', timeline: [], files: [] });
        } catch (error: any) {
            setError(error.message || 'An error occurred. Please try again.');
            window.scrollTo({ top: 0, behavior: 'smooth'});
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatPhoneNumber = (value: string): string => {
        // Remove all non-numeric characters
        const phoneNumber = value.replace(/[^\d]/g, '');
        
        // Limit to 10 digits
        const truncated = phoneNumber.slice(0, 10);
        
        // Don't format if empty or just starting
        if (truncated.length === 0) {
            return '';
        }
        
        // Format based on length
        if (truncated.length <= 3) {
            return truncated;
        } else if (truncated.length <= 6) {
            return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
        } else {
            return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)}-${truncated.slice(6)}`;
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        
        if (name === 'phone') {
            // Special handling for phone to allow natural deletion
            const digitsOnly = value.replace(/[^\d]/g, '');
            
            // If user is deleting (fewer digits than before), allow it
            const currentDigits = formData.phone.replace(/[^\d]/g, '');
            if (digitsOnly.length < currentDigits.length) {
                // Just format what's left
                setFormData({
                    ...formData,
                    [name]: formatPhoneNumber(value)
                });
            } else {
                // Normal formatting for addition
                setFormData({
                    ...formData,
                    [name]: formatPhoneNumber(value)
                });
            }
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    // Timeline event handlers
    const addTimelineEvent = () => {
        const newEvent: TimelineEvent = {
            date: '',
            description: '',
            id: `event-${Date.now()}`
        };
        
        setFormData({
            ...formData,
            timeline: [...formData.timeline, newEvent]
        });
    };

    const updateTimelineEvent = (id: string, field: 'date' | 'description', value: string) => {
        setFormData({
            ...formData,
            timeline: formData.timeline.map(event => 
                event.id === id ? { ...event, [field]: value } : event
            )
        });
    };

    const removeTimelineEvent = (id: string) => {
        setFormData({
            ...formData,
            timeline: formData.timeline.filter(event => event.id !== id)
        });
    };

    if (submitted) {
        return (
            <div className="section intake-section">
                <div className="success-container">
                    <div className="success-icon">✓</div>
                    <h1>Secure Submission Received</h1>
                    <div className="success-content">
                        <p>Your confidential information has been securely transmitted to our office.</p>
                        <p>You will receieve an email copy of your responses shortly.</p>
                    </div>
                    <button 
                        onClick={() => {
                            setSubmitted(false);
                            setError('');
                        }}
                        className="new-submission-btn"
                    >
                        Submit Another Form
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="section intake-section">
            <div className="security-banner">
                <span className="lock-icon">🔒</span>
                <span>Secure & Confidential Submission</span>
            </div>
            
            <h1>Client Intake Form</h1>
            <p className="intro-text">
                All information submitted is confidential. 
                Please provide details about your legal situation so we can better assist you.
                All fields are required.
            </p>

            {error && (
                <div className="error-message">
                    <span>⚠️</span>
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit} className="intake-form">
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="firstName">First Name *</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            maxLength={100}
                            placeholder="Enter your first name"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="lastName">Last Name *</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                            maxLength={100}
                            placeholder="Enter your last name"
                        />
                    </div>


                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="email">Email Address *</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            maxLength={255}
                            placeholder="your.email@example.com"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="phone">Phone Number *</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            maxLength={20}
                            placeholder="(555) 123-4567"
                        />
                    </div>


                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="wrongdoingParty">Wrongdoing Party or Company *</label>
                        <input
                            type="text"
                            id="wrongdoingParty"
                            name="wrongdoingParty"
                            value={formData.wrongdoingParty}
                            onChange={handleChange}
                            required
                            maxLength={255}
                            placeholder="Individual or company name"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="legalIssue">Type of Legal Issue *</label>
                        <select
                            id="legalIssue"
                            name="legalIssue"
                            value={formData.legalIssue}
                            onChange={handleChange}
                            required
                        >
                            <option value="">Select an option</option>
                            <option value="Consumer Disputes">Consumer Disputes</option>
                            <option value="Employment Law">Employment Law</option>
                            <option value="Business Litigation">Business Litigation</option>
                            <option value="Unauthorized Transactions">Unauthorized Transactions</option>
                            <option value="Breach of Contract">Breach of Contract</option>
                            <option value="Privacy Law">Privacy Law</option>
                            <option value="Civil Appeals">Civil Appeals</option>
                            <option value="Unfair Debt Collection">Unfair Debt Collection</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>
                </div>

                <div className="form-group full-width">
                    <label htmlFor="description">Describe Your Legal Situation *</label>
                    <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        rows={6}
                        required
                        maxLength={5000}
                        placeholder="Please provide details about your legal issue, including relevant dates, parties involved, and any documentation you may have..."
                    />
                    <div className="char-count">
                        {formData.description.length}/5000 characters
                    </div>
                </div>

                <div className="form-group full-width file-upload-section">
                    <label>Supporting Documents (Optional)</label>
                    <div className="file-upload-box">
                        <input
                            type="file"
                            id="file-upload"
                            multiple
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.eml"
                            onChange={handleFileChange}
                            className="file-input"
                        />
                        <label htmlFor="file-upload" className="file-upload-label">
                            <div className="upload-text">+ File Upload</div>
                            <div className="upload-description">
                                Upload contracts, emails, photos, or other evidence (PDF, Word, images, text files)
                            </div>
                        </label>
                    </div>

                    {fileError && (
                        <div className="error-message file-error">
                            <span>⚠️</span>
                            {fileError}
                        </div>
                    )}

                    {formData.files.length > 0 && (
                        <div className="selected-files-compact">
                            <strong>{formData.files.length} file{formData.files.length === 1 ? '' : 's'} selected:</strong>
                            {formData.files.map((file, index) => (
                                <span key={index} className="file-tag">
                                    {file.name}
                                    <button
                                        type="button"
                                        className="remove-file-btn-compact"
                                        onClick={() => removeFile(index)}
                                        title="Remove file"
                                    >
                                        ✕
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="form-group full-width timeline-section">
                    <label>Timeline of Events</label>
                    <p className="timeline-description">
                        Add important dates and descriptions to create a timeline of events related to your case. The more you add, the better!
                    </p>
                    
                    {formData.timeline.length === 0 && (
                        <div className="empty-timeline">
                            <p>No timeline events added yet. Use the button below to add events.</p>
                        </div>
                    )}
                    
                    {formData.timeline.map((event, index) => (
                        <div className="timeline-event" key={event.id}>
                            <div className="timeline-event-header">
                                <span className="event-number">Event {index + 1}</span>
                                <button 
                                    type="button" 
                                    className="remove-event-btn"
                                    onClick={() => removeTimelineEvent(event.id)}
                                >
                                    ✕
                                </button>
                            </div>
                            <div className="timeline-event-content">
                                <div className="timeline-form-row">
                                    <div className="form-group">
                                        <label htmlFor={`event-date-${event.id}`}>Date</label>
                                        <input
                                            type="date"
                                            id={`event-date-${event.id}`}
                                            value={event.date}
                                            required
                                            onChange={(e) => updateTimelineEvent(event.id, 'date', e.target.value)}
                                        />
                                    </div>
                                    <div className="form-group timeline-description-field">
                                        <label htmlFor={`event-desc-${event.id}`}>Description</label>
                                        <textarea
                                            id={`event-desc-${event.id}`}
                                            value={event.description}
                                            onChange={(e) => updateTimelineEvent(event.id, 'description', e.target.value)}
                                            rows={2}
                                            required
                                            placeholder="Describe what happened on this date..."
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    <button 
                        type="button" 
                        className="add-event-btn"
                        onClick={addTimelineEvent}
                    >
                        <span className="plus-icon">+</span> Add Event
                    </button>
                </div>

                
                
                <div className="info-box disclaimer">
                    <div className="info-content">
                        <div className="info-text">
                            <strong>Please Note:</strong> This form submission is for case evaluation only and does NOT create an attorney-client relationship. We reserve the right to decline representation.
                        </div>
                    </div>
                </div>

                <div className="recaptcha-container">
                    {siteKey ? (
                        <ReCAPTCHA
                            sitekey={siteKey}
                            onChange={(token: string | null) => setCaptchaToken(token)}  
                        />
                    ) : (
                        <div className="error-message">
                            <span>⚠️</span>
                            reCAPTCHA site key is missing. Please check your environment variables.
                        </div>
                    )}
                </div>

                <button 
                    type="submit" 
                    disabled={isSubmitting} 
                    className="submit-button"
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner"></span>
                            Securely Submitting...
                        </>
                    ) : (
                        <>
                            Submit Form
                        </>
                    )}
                </button>
            </form>
        </div>
    );
}

export default ClientIntake;